import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import L from "leaflet";
import { MapContainer, TileLayer, Circle, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";

// Carte de la zone de livraison en main propre : cercle de 50 km autour de l'atelier
// de Léry + un marqueur par ville (miniature = armoiries de la ville, sinon pastille or).
const LERY = [45.3407383, -73.8113982];
const RADIUS_M = 50000;

const cityIcon = (v) => v.blason
  ? L.divIcon({
      className: "",
      html: `<div style="width:34px;height:34px;border-radius:50%;border:2px solid #d4af37;background:#0d0d0f;overflow:hidden;display:flex;align-items:center;justify-content:center">
               <img src="${v.blason}" alt="" style="width:28px;height:28px;object-fit:contain" loading="lazy" />
             </div>`,
      iconSize: [34, 34], iconAnchor: [17, 17], popupAnchor: [0, -16],
    })
  : L.divIcon({
      className: "",
      html: `<div style="width:14px;height:14px;border-radius:50%;background:#d4af37;border:2px solid #0d0d0f;box-shadow:0 0 6px rgba(212,175,55,.8)"></div>`,
      iconSize: [14, 14], iconAnchor: [7, 7], popupAnchor: [0, -8],
    });

const shopIcon = L.divIcon({
  className: "",
  html: `<div style="width:40px;height:40px;border-radius:50%;background:#d4af37;border:3px solid #0d0d0f;display:flex;align-items:center;justify-content:center;box-shadow:0 0 14px rgba(212,175,55,.9)">
           <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0d0d0f" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/></svg>
         </div>`,
  iconSize: [40, 40], iconAnchor: [20, 20], popupAnchor: [0, -20],
});

export default function DeliveryZoneMap() {
  const { lang } = useLang();
  const fr = lang !== "en";
  const [villes, setVilles] = useState(null);

  useEffect(() => {
    api.get("/villes").then((r) => setVilles(r.data || [])).catch(() => setVilles([]));
  }, []);

  if (!villes) {
    return <div className="h-96 rounded-xl border border-ivory/10 bg-surface animate-pulse" data-testid="delivery-map-loading" />;
  }
  const pts = villes
    .filter((v) => v.lat && v.lng)
    // Seules les villes DANS la zone de main propre ont un marqueur (les pages villes
    // au-delà de 50 km existent pour le SEO, mais ne sont pas dans la zone livrée à la main)
    .filter((v) => Math.hypot((v.lat - LERY[0]) * 111, (v.lng - LERY[1]) * 79) <= RADIUS_M / 1000 + 2);

  return (
    <div className="rounded-xl overflow-hidden border border-gold/25" data-testid="delivery-zone-map">
      <style>{`.leaflet-tile-pane { filter: invert(0.92) hue-rotate(185deg) brightness(0.9) contrast(0.92) saturate(0.35); }
                .leaflet-popup-content-wrapper { background:#14120d; color:#f4f1ea; border:1px solid rgba(212,175,55,.4); border-radius:10px; }
                .leaflet-popup-tip { background:#14120d; }
                .leaflet-popup-content a { color:#d4af37; }`}</style>
      <MapContainer center={LERY} zoom={8} scrollWheelZoom={false}
        style={{ height: 420, background: "#0d0d0f" }}>
        <TileLayer
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />
        <Circle center={LERY} radius={RADIUS_M}
          pathOptions={{ color: "#d4af37", weight: 2, dashArray: "6 6", fillColor: "#d4af37", fillOpacity: 0.06 }} />
        <Marker position={LERY} icon={shopIcon}>
          <Popup>
            <strong>Mr Trophée — {fr ? "Atelier de Léry" : "Léry workshop"}</strong><br />
            {fr ? `Livraison en main propre jusqu'à ${RADIUS_M / 1000} km` : `Hand delivery within ${RADIUS_M / 1000} km`}
          </Popup>
        </Marker>
        {pts.map((v) => (
          <Marker key={v.slug} position={[v.lat, v.lng]} icon={cityIcon(v)}>
            <Popup>
              <strong>{v.ville}</strong><br />
              <Link to={`/villes/${v.slug}`}>
                {fr ? "Voir la page de la ville →" : "View the city page →"}
              </Link>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      <p className="text-[11px] text-ivory/45 bg-surface px-4 py-2.5">
        {fr
          ? "Cercle doré : zone de livraison en main propre (50 km de l'atelier). Les médaillons montrent les armoiries des villes couvertes."
          : "Gold circle: hand-delivery zone (50 km from the workshop). Medallions show the coats of arms of the cities covered."}
      </p>
    </div>
  );
}
